// BEGIN FILE: src/components/HouseScoresCompactView.tsx
import React, { useRef, useState, useCallback } from 'react';
import { Box, Typography, Chip, Card, CardContent, Tooltip, Button } from '@mui/joy';
import {
  computeHouseLFI, getHousePlanetRoles, getFrictionLevel, FRICTION_LEVEL_COLORS,
  type HouseLFIResult,
} from '../astro/karmic/karmicFrictionEngine';import type { HouseInterpretation } from '../utils/fetchHouseInterpretations';import { classifyFunctionalForLagna, type CombustionMap, type ExtendedFuncMap } from '../utils/astroEngine';
import { getHouseLord, computeLordDusthanaPenalties, type LordDusthanaPenalty } from '../utils/lordshipUtils';
import { useIsMobile } from '../hooks/useIsMobile';
import { computeHouseCombinedScore, getHouseRating, computeDashaImpactByHouse, computeTransitImpactByHouse } from '../utils/houseTemporalScores';

interface DashaLevel {
  planet: string;
  occupies?: number;
  rules?: number[];
  aspects?: number[];
}

interface DashaContext {
  MD: DashaLevel;
  AD: DashaLevel;
  PD: DashaLevel;
  SD: DashaLevel;
}

interface PlanetPlacement {
  house: number;
  dignity?: 'exalted' | 'debilitated' | 'own' | 'moolatrikona' | 'friend' | 'neutral' | 'enemy';
}

interface HouseScoresCompactViewProps {
  d1HouseScores: Record<number, number>;
  bhavaBalaRupas: Record<number, number>;
  houseNames: Record<number, string>;
  dashaContext?: DashaContext;
  planetPlacements?: Record<string, PlanetPlacement>;
  ascendantSignIndex?: number;
  combustion?: CombustionMap | null;
  dashaImpactByHouse?: Record<number, number>;
  transitImpactByHouse?: Record<number, number>;
  /** Raw transit planet-to-house positions for inline transit scoring */
  transitPlanetHouses?: Array<{ planet: string; house: number }>;
  /** Natal planet strength percentages (for transit scoring weights) */
  planetStrengthPct?: Partial<Record<string, number>>;
  /** Lord dusthana penalties — overrides rating to 'Needs Attention' when present */
  lordDusthanaPenalties?: Record<number, LordDusthanaPenalty>;
  /** Transit dignities for enhanced scoring (exalted/own/moolatrikona/etc per planet) */
  transitDignities?: Partial<Record<string, string>> | null;
  /** Lagna sign index (1-12) — used for sign-lord friendship scoring */
  transitAscLagnaSign?: number | null;
  /** Combustion map from transit positions */
  transitCombust?: Partial<Record<string, boolean>> | null;
  /** Retrograde map from transit positions (vakri = ×1.5 stronger) */
  transitRetrograde?: Partial<Record<string, boolean>> | null;
  /** Planet friction scores from Karmic Pattern Detection (0-100 per planet) */
  frictionScoresByPlanet?: Record<string, number>;
  /** Opens the Karmic Pattern Detection modal */
  onKarmicPatternClick?: () => void;
  /** House interpretations from Supabase (lord-in-house + sign notes) */
  houseInterpretations?: HouseInterpretation[];
}

// Primary house labels with secondary keywords
const HOUSE_LABELS: Record<number, { primary: string; keywords: string }> = {
  1: { primary: 'Persona', keywords: 'Self, Body, Vitality' },
  2: { primary: 'Wealth', keywords: 'Family, Speech, Assets' },
  3: { primary: 'Communication', keywords: 'Siblings, Courage, Skills' },
  4: { primary: 'Property', keywords: 'Home, Mother, Peace' },
  5: { primary: 'Children', keywords: 'Creativity, Intelligence' },
  6: { primary: 'Health', keywords: 'Service, Obstacles, Enemies' },
  7: { primary: 'Spouse', keywords: 'Partnership, Marriage' },
  8: { primary: 'Longevity', keywords: 'Transformation, Occult' },
  9: { primary: 'Fortune', keywords: 'Father, Luck, Dharma' },
  10: { primary: 'Career', keywords: 'Status, Reputation, Authority' },
  11: { primary: 'Gains', keywords: 'Friends, Aspirations, Income' },
  12: { primary: 'Expenses', keywords: 'Loss, Liberation, Foreign' }
};

/**
 * Modern dashboard 4×3 grid with donut ring + 4-metric bars.
 * Ratings: Excellent (≥75) · Good (60-74) · Fair (30-59) · Needs Attention (<30)
 */

// Parashari aspect offsets (0-based house offset from source house)
const PARASHARI_OFFSETS: Record<string, number[]> = {
  Sun: [6], Moon: [6], Mercury: [6], Venus: [6],
  Mars: [3, 6, 7], Jupiter: [4, 6, 8], Saturn: [2, 6, 9],
  Rahu: [4, 6, 8], Ketu: [4, 6, 8],
};

// Planet short names
const PLANET_ABBR: Record<string, string> = {
  'Sun': 'Su', 'Moon': 'Mo', 'Mars': 'Ma', 'Mercury': 'Me',
  'Jupiter': 'Ju', 'Venus': 'Ve', 'Saturn': 'Sa', 'Rahu': 'Ra', 'Ketu': 'Ke'
};

// Zodiac signs (1-indexed)
const SIGN_NAMES: Record<number, string> = {
  1: 'Aries', 2: 'Taurus', 3: 'Gemini', 4: 'Cancer',
  5: 'Leo', 6: 'Virgo', 7: 'Libra', 8: 'Scorpio',
  9: 'Sagittarius', 10: 'Capricorn', 11: 'Aquarius', 12: 'Pisces',
};

const SIGN_SYMBOLS: Record<number, string> = {
  1: '♈', 2: '♉', 3: '♊', 4: '♋',
  5: '♌', 6: '♍', 7: '♎', 8: '♏',
  9: '♐', 10: '♑', 11: '♒', 12: '♓',
};

// Fire, Earth, Air, Water (triplicities)
const SIGN_ELEMENT: Record<number, { name: string; color: string; bg: string }> = {
  1:  { name: 'Fire',  color: '#dc2626', bg: '#fef2f2' },
  2:  { name: 'Earth', color: '#65a30d', bg: '#f7fee7' },
  3:  { name: 'Air',   color: '#0891b2', bg: '#ecfeff' },
  4:  { name: 'Water', color: '#2563eb', bg: '#eff6ff' },
  5:  { name: 'Fire',  color: '#dc2626', bg: '#fef2f2' },
  6:  { name: 'Earth', color: '#65a30d', bg: '#f7fee7' },
  7:  { name: 'Air',   color: '#0891b2', bg: '#ecfeff' },
  8:  { name: 'Water', color: '#2563eb', bg: '#eff6ff' },
  9:  { name: 'Fire',  color: '#dc2626', bg: '#fef2f2' },
  10: { name: 'Earth', color: '#65a30d', bg: '#f7fee7' },
  11: { name: 'Air',   color: '#0891b2', bg: '#ecfeff' },
  12: { name: 'Water', color: '#2563eb', bg: '#eff6ff' },
};

// Dignity symbols
const DIGNITY_SYMBOL: Record<string, string> = {
  'exalted': '⬆', 'debilitated': '⬇', 'own': '⭐',
  'moolatrikona': '👑', 'friend': '🤝', 'neutral': '⚪', 'enemy': '⚔️'
};

// 4-tier colour theme (pastel borders/accents)
const TIER_THEME = {
  Excellent:        { bg: '#f0fdf4', border: '#86efac', accent: '#22c55e', text: '#15803d' },
  Good:             { bg: '#eff6ff', border: '#93c5fd', accent: '#3b82f6', text: '#1d4ed8' },
  Fair:             { bg: '#fffbeb', border: '#fcd34d', accent: '#f59e0b', text: '#b45309' },
  'Needs Attention':{ bg: '#fef2f2', border: '#fca5a5', accent: '#f87171', text: '#b91c1c' },
};

// Donut ring component
function DonutRing({ score, color, size = 48 }: { score: number; color: string; size?: number }) {
  const cx = size / 2;
  const cy = size / 2;
  const r  = size / 2 - 3;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - Math.min(Math.max(score, 0), 100) / 100);
  const scoreFontSize = size <= 40 ? '10px' : '11px';
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="5" />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={circumference} strokeDashoffset={dashOffset} strokeLinecap="round" />
      </svg>
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: scoreFontSize, fontWeight: 700, lineHeight: 1, color }}>{score}</Typography>
        <Typography sx={{ fontSize: '9px', color: 'text.tertiary', lineHeight: 1 }}>%</Typography>
      </Box>
    </Box>
  );
}

// Single metric bar row
const P_ABBR: Record<string, string> = {
  Sun:'Su', Moon:'Mo', Mercury:'Me', Venus:'Ve', Mars:'Ma',
  Jupiter:'Ju', Saturn:'Sa', Rahu:'Ra', Ketu:'Ke',
};

function FrictionBar({ lfi }: { lfi: HouseLFIResult }) {
  const level = getFrictionLevel(lfi.lfi);
  const color = FRICTION_LEVEL_COLORS[level];
  const barColor = (s: number) => s <= 33 ? '#22c55e' : s <= 60 ? '#f59e0b' : '#ef4444';

  const planetList = (planets: string[]) =>
    planets.length > 0
      ? planets.map(p =>
          lfi.planetScores[p] !== undefined
            ? `${P_ABBR[p] ?? p.slice(0,2)} ${Math.round(lfi.planetScores[p])}%`
            : `${P_ABBR[p] ?? p.slice(0,2)} (no data)`
        ).join(', ')
      : '—';

  const roles = [
    {
      label: 'Res',
      score: lfi.residentScore,
      noData: lfi.residentScore === null && lfi.residentPlanets.length > 0,
      show: lfi.residentPlanets.length > 0,
      tooltip: `Residents: ${planetList(lfi.residentPlanets)}`,
    },
    {
      label: 'Lord',
      score: lfi.lordScore,
      noData: lfi.lordScore === null && lfi.lordPlanet !== null,
      show: lfi.lordPlanet !== null,
      tooltip: lfi.lordPlanet
        ? `Lord (${P_ABBR[lfi.lordPlanet] ?? lfi.lordPlanet}): ${lfi.lordScore !== null ? Math.round(lfi.lordScore) + '%' : 'no data'}`
        : 'Lord: —',
    },
    {
      label: 'Asp',
      score: lfi.aspectorScore,
      noData: lfi.aspectorScore === null && lfi.aspectorPlanets.length > 0,
      show: lfi.aspectorPlanets.length > 0,
      tooltip: `Aspectors: ${planetList(lfi.aspectorPlanets)}`,
    },
  ];

  // Always render all 3 bars for uniform layout across cards
  return (
    <Box sx={{ mb: 1 }}>
      {/* Header: "Life Friction" + combined level + score */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', lineHeight: 1 }}>Life Friction</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 500, color, lineHeight: 1, opacity: 0.85 }}>{level}</Typography>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color, lineHeight: 1 }}>{Math.round(lfi.lfi)}%</Typography>
        </Box>
      </Box>
      {/* One mini-bar per role, side by side */}
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        {roles.map(({ label, score, noData, show, tooltip }) => {
          const pct = score !== null ? Math.min(Math.max(score, 0), 100) : 0;
          const bc = noData ? '#d1d5db' : (score !== null ? barColor(score) : '#d1d5db');
          return (
            <Tooltip key={label} title={show ? tooltip : `${label}: none`} placement="top" arrow size="sm">
              <Box sx={{ flex: 1, minWidth: 0, cursor: show ? 'help' : 'default' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', lineHeight: 1, opacity: show ? 0.7 : 0.35 }}>{label}</Typography>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: show ? (noData ? '#9ca3af' : bc) : 'transparent', lineHeight: 1 }}>
                    {!show ? '—' : noData ? '?' : (score !== null ? `${Math.round(score)}%` : '')}
                  </Typography>
                </Box>
                <Box sx={{ height: 6, borderRadius: '3px', bgcolor: 'rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                  {show && (
                    <Box sx={{
                      width: noData ? '100%' : `${pct}%`,
                      height: '100%', borderRadius: '2px',
                      bgcolor: bc, opacity: noData ? 0.2 : 1,
                    }} />
                  )}
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

function MetricBar({ label, displayVal, fillPct, color, bipolar }: {
  label: string; displayVal: string; fillPct: number; color: string; bipolar: boolean;
}) {
  const clampedPct = Math.min(Math.max(fillPct, 0), 100);
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', lineHeight: 1 }}>{label}</Typography>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color, lineHeight: 1 }}>{displayVal}</Typography>
      </Box>
      <Box sx={{ height: 7, borderRadius: '4px', bgcolor: 'rgba(0,0,0,0.07)', position: 'relative', overflow: 'hidden' }}>
        {bipolar ? (
          <>
            <Box sx={{ position: 'absolute', top: 0, left: '49.5%', width: '1px', height: '100%', bgcolor: 'rgba(0,0,0,0.18)', zIndex: 1 }} />
            {clampedPct !== 50 && (
              <Box sx={{
                position: 'absolute', top: 0, height: '100%', borderRadius: '3px',
                left: clampedPct < 50 ? `${clampedPct}%` : '50%',
                width: `${Math.abs(clampedPct - 50)}%`,
                bgcolor: clampedPct > 50 ? color : '#fca5a5',
              }} />
            )}
          </>
        ) : (
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: `${clampedPct}%`, height: '100%', borderRadius: '3px', bgcolor: color }} />
        )}
      </Box>
    </Box>
  );
}

export default function HouseScoresCompactView({
  d1HouseScores,
  bhavaBalaRupas,
  houseNames,
  dashaContext,
  planetPlacements = {},
  ascendantSignIndex,
  combustion,
  dashaImpactByHouse: dashaImpactByHouseProp,
  transitImpactByHouse: transitImpactByHouseProp,
  transitPlanetHouses,
  planetStrengthPct,
  lordDusthanaPenalties,
  transitDignities,
  transitAscLagnaSign,
  transitCombust,
  transitRetrograde,
  frictionScoresByPlanet,
  onKarmicPatternClick,
  houseInterpretations,
}: HouseScoresCompactViewProps): React.ReactElement {

  const { isMobile } = useIsMobile();
  // True mouse/pointer device — independent of viewport width (DevTools sidebar doesn't affect this)
  const [hasPointer, setHasPointer] = React.useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(hover: hover) and (pointer: fine)').matches : true
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const handler = (e: MediaQueryListEvent) => setHasPointer(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Mobile slider state
  const sliderRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const handleSliderScroll = useCallback(() => {
    const el = sliderRef.current;
    if (!el) return;
    setActiveSlide(Math.round(el.scrollLeft / el.clientWidth));
  }, []);

  const functionalClassification = ascendantSignIndex
    ? classifyFunctionalForLagna(ascendantSignIndex)
    : null;

  // Compute dasha impact inline from dashaContext + lagna functional map.
  const dashaImpactByHouse = React.useMemo(() => {
    if (!dashaContext || !functionalClassification) return dashaImpactByHouseProp;
    return computeDashaImpactByHouse(
      dashaContext as any,
      functionalClassification as Record<string, string>,
    );
  }, [dashaContext, functionalClassification, dashaImpactByHouseProp]);

  // Compute transit impact inline from raw transit positions.
  const transitImpactByHouse = React.useMemo(() => {
    if (!transitPlanetHouses || transitPlanetHouses.length === 0 || !functionalClassification) {
      return transitImpactByHouseProp;
    }
    return computeTransitImpactByHouse(
      transitPlanetHouses,
      functionalClassification as Record<string, string>,
      planetStrengthPct ?? null,
      transitDignities ?? null,
      transitAscLagnaSign ?? null,
      transitCombust ?? null,
      transitRetrograde ?? null,
    );
  }, [transitPlanetHouses, functionalClassification, planetStrengthPct, transitImpactByHouseProp,
      transitDignities, transitAscLagnaSign, transitCombust, transitRetrograde]);

  const houses = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  // Compute lord dusthana afflictions directly from existing props (more reliable than the external prop chain)
  const localLordDusthanaPenalties = React.useMemo(() => {
    if (!ascendantSignIndex || !planetPlacements || Object.keys(planetPlacements).length === 0) return {};
    const simplePlacements: Record<string, { house: number }> = {};
    Object.entries(planetPlacements).forEach(([p, pl]) => { simplePlacements[p] = { house: pl.house }; });
    return computeLordDusthanaPenalties(ascendantSignIndex, simplePlacements);
  }, [ascendantSignIndex, planetPlacements]);

  // Pre-compute house LFIs from planet friction scores
  const houseLFIs = React.useMemo<Record<number, HouseLFIResult>>(() => {
    if (!frictionScoresByPlanet || Object.keys(frictionScoresByPlanet).length === 0 || !ascendantSignIndex) return {};
    const result: Record<number, HouseLFIResult> = {};
    for (let h = 1; h <= 12; h++) {
      const roles = getHousePlanetRoles(h, planetPlacements as Record<string, { house: number }>, ascendantSignIndex);
      result[h] = computeHouseLFI(roles, frictionScoresByPlanet);
    }
    return result;
  }, [frictionScoresByPlanet, planetPlacements, ascendantSignIndex]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', p: isMobile ? 1 : 1.5, m: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
      {onKarmicPatternClick && (
        <Box sx={{ mb: isMobile ? 2.5 : 1, display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
          <Button
            size="sm"
            data-audio-cue="home-karmic-friction-btn"
            onClick={onKarmicPatternClick}
            sx={{
              fontSize: isMobile ? '14px' : '11px',
              py: isMobile ? '10px' : '5px',
              px: '10px',
              width: isMobile ? '100%' : 'auto',
              background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
              color: 'white',
              borderRadius: isMobile ? '10px' : '8px',
              letterSpacing: isMobile ? '0.02em' : 'normal',
              '&:hover': { background: 'linear-gradient(135deg, #312e81 0%, #4338ca 100%)' },
            }}
          >
            🔮 Get Karmic Friction Index
          </Button>
        </Box>
      )}
      <Box sx={isMobile ? { position: 'relative' } : {}}>
        {isMobile && (
          <Box
            component="button"
            onClick={() => {
              const el = sliderRef.current; if (!el || activeSlide <= 0) return;
              const n = activeSlide - 1;
              el.scrollTo({ left: n * el.clientWidth, behavior: 'smooth' });
              setActiveSlide(n);
            }}
            sx={{
              display: activeSlide > 0 ? 'flex' : 'none',
              alignItems: 'center', justifyContent: 'center',
              position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)',
              zIndex: 10, width: '28px', height: '28px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.88)',
              boxShadow: '0 1px 6px rgba(0,0,0,0.20)',
              border: 'none', cursor: 'pointer', p: 0,
              fontSize: '22px', lineHeight: 1, color: '#374151',
              backdropFilter: 'blur(4px)',
            }}
          >
            {String.fromCharCode(8249)}
          </Box>
        )}
        <Box
          ref={isMobile ? sliderRef : undefined}
          onScroll={isMobile ? handleSliderScroll : undefined}
          sx={isMobile ? {
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            overflowX: 'scroll',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          } : {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 1.25,
          }}
        >
        {houses.map((h) => {
          const bhavaBala    = bhavaBalaRupas[h] ?? 0;
          const planetImpact = d1HouseScores[h] ?? 0;
          const dashaImpact  = dashaImpactByHouse?.[h] ?? 50;
          const transitImpact = transitImpactByHouse?.[h] ?? 50;

          const combined = computeHouseCombinedScore(bhavaBala, planetImpact, dashaImpact, transitImpact);
          const baseRating = getHouseRating(combined);
          const affliction = localLordDusthanaPenalties[h] ?? lordDusthanaPenalties?.[h];
          // Rating override only when Jupiter does NOT aspect this house
          const rating = (affliction && !affliction.jupiterProtected) ? 'Needs Attention' as const : baseRating;
          const theme = TIER_THEME[rating];

          // Bipolar fill positions (0-100, 50=neutral)
          const planetFillPct = ((planetImpact + 50) / 100) * 100;
          const dashaFillPct  = dashaImpact;
          const transitFillPct = transitImpact;
          // Signed display: −50..+50 with 0 = neutral
          const dashaDisplay   = (() => { const d = Math.round(dashaImpact - 50); return (d >= 0 ? '+' : '') + d; })();
          const transitDisplay = (() => { const d = Math.round(transitImpact - 50); return (d >= 0 ? '+' : '') + d; })();

          // Dasha activation
          const houseActivation: Array<{ level: string; planet: string; reason: string }> = [];
          if (dashaContext) {
            for (const [level, data] of Object.entries(dashaContext) as Array<[string, DashaLevel]>) {
              const reasons: string[] = [];
              if (data.occupies === h) reasons.push('occupies');
              if (data.rules?.includes(h)) reasons.push('rules');
              if (data.aspects?.includes(h)) reasons.push('aspects');
              if (reasons.length > 0) houseActivation.push({ level, planet: data.planet, reason: reasons.join(', ') });
            }
          }
          const isActivated = houseActivation.length > 0;

          // Occupying planets
          const houseLord = ascendantSignIndex ? getHouseLord(h as any, ascendantSignIndex) : null;
          const lordHouseNum = houseLord ? (planetPlacements?.[houseLord]?.house ?? null) : null;

          const occupyingPlanets = Object.entries(planetPlacements)
            .filter(([, pl]) => pl.house === h)
            .map(([planet, pl]) => {
              const functional = functionalClassification
                ? (functionalClassification[planet as keyof ExtendedFuncMap] || 'neutral')
                : 'neutral';
              const isCombust = combustion?.[planet as keyof typeof combustion]?.combust || false;
              return { planet, dignity: pl.dignity, functional, combust: isCombust };
            });

          const aspectingPlanets = Object.entries(planetPlacements)
            .filter(([, pl]) => pl.house !== h)
            .filter(([planet, pl]) => {
              const offsets = PARASHARI_OFFSETS[planet] || [6];
              return offsets.some(off => ((pl.house - 1 + off) % 12) + 1 === h);
            })
            .map(([planet]) => {
              const functional = functionalClassification
                ? (functionalClassification[planet as keyof ExtendedFuncMap] || 'neutral')
                : 'neutral';
              return { planet, functional };
            });

          const getFunctionalColor = (func: string, combust: boolean) => {
            if (combust) return '#9ca3af';
            switch (func) {
              case 'benefic': return '#10b981';
              case 'malefic': return '#ef4444';
              default:        return '#f59e0b';
            }
          };

          return (
            <Card key={h} variant="outlined" sx={{
              background: theme.bg,
              borderColor: theme.border,
              borderWidth: '2px',
              transition: 'all 0.2s ease',
              position: 'relative',
              overflow: 'visible',
              // Mobile: full-width snap card; desktop: normal grid cell
              ...(isMobile ? {
                flex: '0 0 100%',
                width: '100%',
                minWidth: '100%',
                boxSizing: 'border-box',
                scrollSnapAlign: 'start',
                scrollSnapStop: 'always',
              } : {
                minWidth: 0,
              }),
              '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 12px -2px ${theme.accent}40`, borderWidth: '2.5px' }
            }}>
              <CardContent sx={{ p: isMobile ? '14px 14px' : 1, gap: 0, position: 'relative' }}>

                {/* House number + sign + element strip */}
                {(() => {
                  const signNum = ascendantSignIndex
                    ? ((ascendantSignIndex - 1 + h - 1) % 12) + 1
                    : null;
                  const elem = signNum ? SIGN_ELEMENT[signNum] : null;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: isMobile ? 1 : 0.6, flexWrap: 'wrap' }}>
                      {/* House number badge */}
                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center',
                        bgcolor: theme.accent, color: 'white',
                        borderRadius: '5px', px: isMobile ? '8px' : '5px', py: isMobile ? '3px' : '1px',
                        fontSize: isMobile ? '11px' : '8.5px', fontWeight: 800, lineHeight: 1.4,
                        letterSpacing: '0.4px',
                      }}>
                        H{h}
                      </Box>
                      {signNum && (
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', gap: '3px',
                          bgcolor: 'rgba(0,0,0,0.05)', borderRadius: '5px', px: isMobile ? '8px' : '5px', py: isMobile ? '3px' : '1px',
                          fontSize: isMobile ? '11px' : '8.5px', fontWeight: 700, lineHeight: 1.4,
                          color: 'text.primary',
                        }}>
                          <span style={{ fontSize: isMobile ? '13px' : '10px' }}>{SIGN_SYMBOLS[signNum]}</span>
                          {SIGN_NAMES[signNum]}
                        </Box>
                      )}
                      {elem && (
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center',
                          bgcolor: elem.bg, color: elem.color,
                          border: `1px solid ${elem.color}33`,
                          borderRadius: '5px', px: isMobile ? '8px' : '5px', py: isMobile ? '3px' : '1px',
                          fontSize: isMobile ? '10px' : '8px', fontWeight: 700, lineHeight: 1.4,
                          letterSpacing: '0.2px',
                        }}>
                          {elem.name}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
                {/* Lord affliction warning chip */}
                {affliction && (() => {
                  const off = (affliction.lordHouse - h + 12) % 12;
                  const nth = off === 5 ? '6th' : off === 7 ? '8th' : '12th';
                  const chipText = affliction.jupiterProtected
                    ? `Lord in dusthana (${nth} from own) ♃`
                    : `Lord in dusthana (${nth} from own)`;
                  return (
                    <Tooltip title={affliction.jupiterProtected ? `${affliction.reason} — protected by Jupiter aspect` : affliction.reason} placement="top" arrow size="sm">
                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: '3px', mb: 0.5,
                        bgcolor: affliction.jupiterProtected ? '#f0fdf4'
                          : affliction.severity === 'critical' ? '#fef2f2'
                          : affliction.severity === 'severe' ? '#fff7ed' : '#fffbeb',
                        border: `1px solid ${affliction.jupiterProtected ? '#86efac'
                          : affliction.severity === 'critical' ? '#fca5a5'
                          : affliction.severity === 'severe' ? '#fdba74' : '#fcd34d'}`,
                        borderRadius: '5px', px: '5px', py: '2px', cursor: 'help',
                      }}>
                        <span style={{ fontSize: '9px' }}>{affliction.jupiterProtected ? '♃' : '⚠'}</span>
                        <Typography sx={{ fontSize: '8px', fontWeight: 700,
                          color: affliction.jupiterProtected ? '#15803d'
                            : affliction.severity === 'critical' ? '#b91c1c'
                            : affliction.severity === 'severe' ? '#c2410c' : '#b45309',
                          lineHeight: 1 }}>
                          {chipText}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })()}

                {/* Header: label + donut */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: isMobile ? 1 : 0.4 }}>
                  <Box sx={{ flex: 1, pr: 0.5 }}>
                    <Typography sx={{ fontFamily: '"Raleway", sans-serif', color: theme.text, fontWeight: 700, fontSize: isMobile ? '1.05rem' : '0.84rem', textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.1 }}>
                      {HOUSE_LABELS[h]?.primary || `H${h}`}
                    </Typography>
                    <Typography sx={{ fontFamily: '"Cormorant Garamond", "Garamond", serif', color: theme.text, opacity: 0.65, fontSize: isMobile ? '11px' : '9.5px', fontStyle: 'italic', lineHeight: 1.2, mt: isMobile ? 0.5 : 0.15 }}>
                      {HOUSE_LABELS[h]?.keywords || ''}
                    </Typography>
                    {houseLord && lordHouseNum && (() => {
                      const off = (lordHouseNum - h + 12) % 12;
                      const nth = off === 0 ? 'own' : off === 1 ? '2nd' : off === 2 ? '3rd'
                        : off === 3 ? '4th' : off === 4 ? '5th' : off === 5 ? '6th'
                        : off === 6 ? '7th' : off === 7 ? '8th' : off === 8 ? '9th'
                        : off === 9 ? '10th' : off === 10 ? '11th' : '12th';
                      const lordLabel = `Lord ${PLANET_ABBR[houseLord] || houseLord} in H${lordHouseNum} (${nth} from own)`;

                      // Look up lordship interpretation for hover tooltip — only on pointer (mouse) devices
                      const hInterp = hasPointer ? (houseInterpretations?.find(hi => hi.house === h) ?? null) : null;
                      const lih = hInterp?.lordship?.[0];
                      const sNote = hInterp?.signNote;
                      const hasTooltipContent = hasPointer && !!(lih?.interpretation || lih?.overview || lih?.strengths || lih?.challenges || sNote);

                      if (!hasTooltipContent) return (
                        <Typography sx={{
                          fontSize: isMobile ? '11px' : '9.5px', fontWeight: 700, color: theme.accent,
                          lineHeight: 1, mt: isMobile ? 1 : 0.75,
                          display: 'block',
                        }}>
                          {lordLabel}
                        </Typography>
                      );

                      const tooltipTitle = (
                        <Box sx={{ p: 0.5 }}>
                          {lih?.position && (
                            <Typography sx={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 0.5 }}>
                              {lih.position}
                            </Typography>
                          )}
                          {(lih?.interpretation || lih?.overview) && (
                            <Typography sx={{ fontSize: '12px', fontStyle: 'italic', lineHeight: 1.55, color: 'text.primary', mb: (lih?.strengths || lih?.challenges || sNote) ? 0.75 : 0 }}>
                              {lih.interpretation || lih.overview}
                            </Typography>
                          )}
                          {lih?.strengths && (
                            <Typography sx={{ fontSize: '11px', lineHeight: 1.5, color: 'text.secondary', mb: (lih?.challenges || sNote) ? 0.5 : 0 }}>
                              <strong>Strengths:</strong> {lih.strengths}
                            </Typography>
                          )}
                          {lih?.challenges && (
                            <Typography sx={{ fontSize: '11px', lineHeight: 1.5, color: 'text.secondary', mb: sNote ? 0.5 : 0 }}>
                              <strong>Challenges:</strong> {lih.challenges}
                            </Typography>
                          )}
                          {sNote && (
                            <Typography sx={{ fontSize: '11px', fontStyle: 'italic', lineHeight: 1.5, color: 'text.secondary' }}>
                              {sNote}
                            </Typography>
                          )}
                        </Box>
                      );

                      return (
                        <Tooltip placement="bottom" arrow variant="outlined" sx={{ maxWidth: 320 }} title={tooltipTitle}>
                          <Typography component="span" sx={{
                            fontSize: '9.5px', fontWeight: 700, color: theme.accent,
                            lineHeight: 1, mt: 0.75,
                            cursor: 'help',
                            textDecoration: 'underline dotted',
                            textUnderlineOffset: '2px',
                            display: 'block',
                          }}>
                            {lordLabel}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </Box>
                  <DonutRing score={combined} color={theme.accent} size={isMobile ? 60 : 48} />
                </Box>

                {/* 4 metric bars */}
                <MetricBar label="Bhava Bala" displayVal={`${Math.round(bhavaBala)}%`} fillPct={bhavaBala} color="#60a5fa" bipolar={false} />
                <MetricBar label="Planet Impact" displayVal={`${planetImpact > 0 ? '+' : ''}${Math.round(planetImpact)}`} fillPct={planetFillPct} color="#fbbf24" bipolar={true} />
                <MetricBar label="Dasha" displayVal={dashaDisplay} fillPct={dashaFillPct} color="#a78bfa" bipolar={true} />
                <MetricBar label="Transit" displayVal={transitDisplay} fillPct={transitFillPct} color="#22d3ee" bipolar={true} />
                {houseLFIs[h] && <FrictionBar lfi={houseLFIs[h]} />}

                {/* Planet chips: occupying (solid, left) + aspecting (outlined, right) */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: isMobile ? 1 : 0.4, mb: isMobile ? 1 : 0.5, gap: 0.5 }}>
                  {/* Occupying planets */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                    {occupyingPlanets.map(({ planet, dignity, functional, combust }) => (
                      <Tooltip key={planet} title={`${planet}${dignity ? ` (${dignity})` : ''}${combust ? ' — COMBUST' : ''} · Occupies H${h}`} placement="top" arrow size="sm">
                        <Chip size="sm" variant="solid" sx={{
                          bgcolor: getFunctionalColor(functional, combust),
                          color: 'white', fontWeight: 700, fontSize: isMobile ? '11px' : '9.5px',
                          minHeight: isMobile ? '20px' : '14px', height: isMobile ? '20px' : '14px', px: isMobile ? 1 : 0.5, py: 0,
                          borderRadius: '5px', cursor: 'help',
                          border: combust ? '1.5px dashed rgba(255,255,255,0.6)' : 'none',
                          opacity: combust ? 0.75 : 1,
                        }}>
                          {PLANET_ABBR[planet] || planet.slice(0, 2)}
                          {dignity && DIGNITY_SYMBOL[dignity] && <span style={{ marginLeft: '2px', fontSize: '9px' }}>{DIGNITY_SYMBOL[dignity]}</span>}
                          {combust && <span style={{ marginLeft: '2px', fontSize: '8px' }}>🔥</span>}
                          {planetStrengthPct?.[planet] != null && (
                            <span style={{ marginLeft: '2px', fontSize: '7.5px', opacity: 0.85 }}>
                              ({Math.round(planetStrengthPct[planet]!)})
                            </span>
                          )}
                        </Chip>
                      </Tooltip>
                    ))}
                  </Box>
                  {/* Aspecting planets */}
                  {aspectingPlanets.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, justifyContent: 'flex-end' }}>
                      {aspectingPlanets.map(({ planet, functional }) => (
                        <Tooltip key={planet} title={`${planet} aspects H${h} (from H${planetPlacements[planet]?.house})`} placement="top" arrow size="sm">
                          <Chip size="sm" variant="outlined" sx={{
                            borderColor: getFunctionalColor(functional, false),
                            color: getFunctionalColor(functional, false),
                            fontWeight: 600, fontSize: '7px',
                            minHeight: '13px', height: '13px', px: '3px', py: 0,
                            borderRadius: '4px', cursor: 'help', bgcolor: 'transparent',
                          }}>
                            {PLANET_ABBR[planet] || planet.slice(0, 2)}
                          </Chip>
                        </Tooltip>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Bottom badges */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, alignItems: 'center', justifyContent: 'space-between', mt: 0.25 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.4 }}>
                    {isActivated && houseActivation.map((act, idx) => {
                      const score = planetStrengthPct?.[act.planet];
                      const scoreStr = score != null ? `(${score > 0 ? '+' : ''}${Math.round(score)})` : '';
                      const LEVEL_COLORS: Record<string, { bg: string; color: string }> = {
                        MD: { bg: '#8b5cf6', color: 'white' },
                        AD: { bg: '#3b82f6', color: 'white' },
                        PD: { bg: '#f59e0b', color: '#78350f' },
                        SD: { bg: '#ef4444', color: 'white' },
                      };
                      const col = LEVEL_COLORS[act.level] || { bg: '#6b7280', color: 'white' };
                      const abbr = PLANET_ABBR[act.planet] || act.planet.slice(0, 2);
                      return (
                        <Tooltip key={idx} placement="top" arrow title={
                          <Typography level="body-xs" sx={{ fontSize: '10px', color: 'white' }}>
                            <strong>{act.level}</strong>: {act.planet} {act.reason} H{h}{scoreStr ? ` · net ${scoreStr}` : ''}
                          </Typography>
                        }>
                          <Chip size="sm" variant="solid"
                            sx={{ bgcolor: col.bg, color: col.color, fontWeight: 700, fontSize: isMobile ? '10px' : '7.5px',
                                  height: isMobile ? '20px' : '15px', px: isMobile ? '8px' : '5px', py: 0, cursor: 'help', borderRadius: '5px',
                                  letterSpacing: '0.3px', display: 'flex', alignItems: 'center', lineHeight: 1 }}>
                            {act.level}·{abbr}{scoreStr}
                          </Chip>
                        </Tooltip>
                      );
                    })}
                  </Box>
                  <Chip size="sm" variant="solid" sx={{
                    flexShrink: 0, bgcolor: theme.accent, color: 'white', fontWeight: 700, fontSize: isMobile ? '10px' : '7.5px',
                    height: isMobile ? '20px' : '15px', px: isMobile ? '8px' : '5px', py: 0, borderRadius: '5px',
                    textTransform: 'uppercase', letterSpacing: '0.3px',
                    display: 'flex', alignItems: 'center', lineHeight: 1
                  }}>
                    {rating}
                  </Chip>
                </Box>

                {/* House interpretations — mobile only */}
                {isMobile && (() => {
                  const interp = houseInterpretations?.find(hi => hi.house === h);
                  if (!interp) return null;
                  const lih = interp.lordship?.[0];
                  const signNote = interp.signNote;
                  const lihText = lih?.interpretation || lih?.overview;
                  if (!lihText && !signNote) return null;
                  return (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      {lihText && (
                        <>
                          <Typography sx={{
                            fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em',
                            textTransform: 'uppercase', color: 'text.secondary', mb: 0.5,
                          }}>
                            {lih.position}
                          </Typography>
                          <Typography sx={{
                            fontSize: '11px', color: 'text.secondary',
                            lineHeight: 1.55, mb: signNote ? 1 : 0,
                          }}>
                            {lihText}
                          </Typography>
                        </>
                      )}
                      {signNote && (
                        <Typography sx={{
                          fontSize: '11px', fontStyle: 'italic', color: 'text.tertiary',
                          lineHeight: 1.55,
                        }}>
                          {signNote}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          );
        })}
        </Box>
        {isMobile && (
          <Box
            component="button"
            onClick={() => {
              const el = sliderRef.current; if (!el || activeSlide >= 11) return;
              const n = activeSlide + 1;
              el.scrollTo({ left: n * el.clientWidth, behavior: 'smooth' });
              setActiveSlide(n);
            }}
            sx={{
              display: activeSlide < 11 ? 'flex' : 'none',
              alignItems: 'center', justifyContent: 'center',
              position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
              zIndex: 10, width: '28px', height: '28px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.88)',
              boxShadow: '0 1px 6px rgba(0,0,0,0.20)',
              border: 'none', cursor: 'pointer', p: 0,
              fontSize: '22px', lineHeight: 1, color: '#374151',
              backdropFilter: 'blur(4px)',
            }}
          >
            {String.fromCharCode(8250)}
          </Box>
        )}
      </Box>

      {/* Mobile dot indicators */}
      {isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: '5px', mt: 1 }}>
          {houses.map((h, i) => (
            <Box
              key={h}
              onClick={() => {
                const el = sliderRef.current;
                if (!el) return;
                el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
                setActiveSlide(i);
              }}
              sx={{
                width: activeSlide === i ? '18px' : '6px',
                height: '6px',
                borderRadius: '3px',
                bgcolor: activeSlide === i ? 'primary.500' : 'neutral.300',
                transition: 'width 0.25s ease, background-color 0.25s ease',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            />
          ))}
        </Box>
      )}

      {/* Footer legend */}
      <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '9px' }}>
          🩵 Bhava Bala · 🟡 Planet Impact · 🟣 Dasha (±50) · 🔵 Transit (±50){Object.keys(houseLFIs).length > 0 ? ' · 🔮 Life Friction (dots = Res/Lord/Asp roles)' : ''} · Donut = Combined
        </Typography>
      </Box>
    </Box>
  );
}


